import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  InputAdornment,
  Typography,
  Box
} from '@mui/material';
import { format } from 'date-fns';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../utils/formatters';

export default function EditModal({ open, onClose, emprestimo, onSuccess }) {
  const [formData, setFormData] = useState({
    novo_valor_total: '',
    nova_taxa_juros: '',
    nova_qtd_parcelas: '',
    data_proximo_vencimento: format(new Date(), 'yyyy-MM-dd'),
    nova_periodicidade: 'mensal',
    motivo_edicao: ''
  });

  const [totalPago, setTotalPago] = useState(0);

  useEffect(() => {
    if (emprestimo && open) {
      // Calcular o total já pago (apenas principal)
      const parcelasPagas = emprestimo.parcelas?.filter(p => p.status === 'pago' || p.status === 'pago_parcial') || [];
      const pagoPrincipal = parcelasPagas.reduce((sum, p) => sum + Number(p.valor_original || 0), 0);
      setTotalPago(pagoPrincipal);

      setFormData({
        novo_valor_total: emprestimo.valor_total || '',
        nova_taxa_juros: emprestimo.taxa_juros || '',
        nova_qtd_parcelas: (emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas) - parcelasPagas.length || '',
        data_proximo_vencimento: format(new Date(), 'yyyy-MM-dd'),
        nova_periodicidade: emprestimo.periodicidade || 'mensal',
        motivo_edicao: ''
      });
    }
  }, [emprestimo, open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Number(formData.novo_valor_total) < totalPago) {
      toast.error('O novo valor total não pode ser menor que o capital já amortizado.');
      return;
    }
    try {
      await api.put(`/emprestimos/${emprestimo.id}/editar`, formData);
      toast.success('Empréstimo editado com sucesso!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao editar empréstimo');
    }
  };

  if (!emprestimo) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'warning.main', fontWeight: 'bold' }}>Renegociar / Editar Empréstimo</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
            <Typography variant="body2" gutterBottom>
              <b>Atenção:</b> Esta ação recalculará as parcelas pendentes com base no novo saldo.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Valor Principal Já Pago: <b>{formatCurrency(totalPago)}</b>
            </Typography>
            <Typography variant="body2" color="error">
              O novo valor total deve ser no mínimo {formatCurrency(totalPago)}.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Novo Valor Total Original"
                name="novo_valor_total"
                type="number"
                value={formData.novo_valor_total}
                onChange={handleInputChange}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nova Taxa de Juros"
                name="nova_taxa_juros"
                type="number"
                value={formData.nova_taxa_juros}
                onChange={handleInputChange}
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nova Qtd. Parcelas (Restantes)"
                name="nova_qtd_parcelas"
                type="number"
                value={formData.nova_qtd_parcelas}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data do Próx. Vencimento"
                name="data_proximo_vencimento"
                type="date"
                value={formData.data_proximo_vencimento}
                onChange={handleInputChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Nova Periodicidade"
                name="nova_periodicidade"
                value={formData.nova_periodicidade}
                onChange={handleInputChange}
                required
              >
                <MenuItem value="mensal">Mensal</MenuItem>
                <MenuItem value="quinzenal">Quinzenal</MenuItem>
                <MenuItem value="semanal">Semanal</MenuItem>
                <MenuItem value="diario">Diário</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Motivo da Edição"
                name="motivo_edicao"
                value={formData.motivo_edicao}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">Cancelar</Button>
          <Button type="submit" variant="contained" color="warning">
            Confirmar Edição
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
